import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionActions from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Pagination from "@mui/material/Pagination";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import moment from "moment-timezone";

import Spark from "./charts/spark";
import SimpleTable from "views/Components/SimpleTable";

import { abbreviateNumber, navToInvestigation } from "./helpers";
import { entityFields } from "./defs";

const PREFIX = "SimpleSearchStyles";
const fieldControlClasses = {
  button: `${PREFIX}-button`,
};

const overviewClasses = {
  italic: `${PREFIX}-italic`,
};

const popoverClasses = {
  listRoot: `${PREFIX}-listRoot`,
  listItemSecondaryAction: `${PREFIX}-listItemSecondaryAction`,
  listItem: `${PREFIX}-listItem`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${fieldControlClasses.button}`]: {
    margin: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  [`& .${overviewClasses.italic}`]: {
    fontStyle: "italic",
  },
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  [`& .${popoverClasses.listRoot}`]: {
    minWidth: "450px",
  },
  [`& .${popoverClasses.listItemSecondaryAction}`]: {
    visibility: "hidden",
    position: "unset",
    transform: "none",
    minWidth: "120px",
  },
  [`& .${popoverClasses.listItem}`]: {
    visibility: "inherit",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover .SimpleSearchStyles-listItemSecondaryAction": {
      visibility: "visible",
    },
  },
}));

const FieldControl = ({ column, data, callback }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortedIndex, setSortedIndex] = useState([]);
  const [values, setValues] = useState({});
  const [filterState, setFilterState] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    readData();
  }, [data]);

  useEffect(() => {
    const checked = Object.values(values).filter((o) => o.checked);
    /***
     * -1 : all selected
     *  0 : none selected
     *  1+: more than one, but not all selected
     */
    var state = -1;
    if (sortedIndex.length > checked.length) {
      if (checked.length === 0) {
        state = 0;
      } else {
        state = checked.length;
      }
    }
    setFilterState(state);
  }, [values]);

  const readData = () => {
    const _uniqueSorted = Object.keys(data).map((k) => {
      return { value: k, count: data[k].count };
    });
    _uniqueSorted.sort((a, b) => (a.count > b.count ? -1 : 1));
    setSortedIndex(_uniqueSorted.map((o) => o.value));
    setValues(data);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCancel = () => {
    readData();
    handleClose();
  };

  const handleApply = () => {
    callback(values);
    handleClose();
  };

  const handleToggle = (item, setOnly = false) => {
    if (setOnly) {
      Object.values(values).forEach((v) => (v.checked = false));
      values[item.toLowerCase()].checked = true;
    } else {
      values[item.toLowerCase()].checked = !values[item].checked;
    }
    setValues({ ...values });
  };

  const handleToggleAll = () => {
    const uncheck = filterState != 0;
    Object.values(values).forEach((v) => (v.checked = !uncheck));
    setValues({ ...values });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const open = Boolean(anchorEl);
  const id = open ? "field-popover" : undefined;
  return (
    <Root sx={{ display: "inline-flex" }}>
      <Button
        aria-describedby={id}
        onClick={handleClick}
        className={fieldControlClasses.button}
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
      >
        {column}
        {filterState > 0 && <span> ({filterState})</span>}
      </Button>
      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Card>
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={filterState < 0}
                  onChange={handleToggleAll}
                  name="checkedF"
                  indeterminate={filterState > 0}
                />
              }
              label={`Select ${column}`}
            />
            <List className={popoverClasses.listRoot}>
              {sortedIndex
                .slice(
                  itemsPerPage * currentPage - itemsPerPage,
                  itemsPerPage * currentPage
                )
                .map((key) => (
                  <ListItem
                    key={key}
                    role={undefined}
                    dense
                    button
                    onClick={() => handleToggle(key)}
                    classes={{
                      container: popoverClasses.listItem,
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={values[key].checked}
                        //tabIndex={-1}
                        disableRipple
                        inputProps={{
                          "aria-labelledby": `checkbox-list-label-${key}`,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={`checkbox-list-label-${key}`}
                      primary={`${values[key].value} (${values[key].count})`}
                    />
                    <ListItemSecondaryAction
                      className={popoverClasses.listItemSecondaryAction}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleToggle(key, true)}
                      >
                        Exclude others
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          </CardContent>
          <CardActions>
            <Button variant="outlined" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Pagination
              count={Math.ceil(sortedIndex.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
            />
          </CardActions>
        </Card>
      </StyledPopover>
    </Root>
  );
};

const OverviewContolBar = ({ fieldData, setOverviewContext }) => {
  //const indexedColumns = Object.fromEntries(columns.map((o) => [o.field, o]));
  const columns = Object.keys(fieldData);
  const callback = (fieldName, data) => {
    fieldData[fieldName] = data;
    setOverviewContext({ ...fieldData });
  };
  return (
    <>
      {Object.keys(fieldData)
        .filter((fieldName) => columns.includes(fieldName))
        .map((fieldName) => (
          <FieldControl
            key={fieldName}
            column={fieldName}
            data={{ ...fieldData[fieldName] }}
            callback={(data) => {
              callback(fieldName, data);
            }}
          />
        ))}
    </>
  );
};

export const LegacyOverview = ({
  entity,
  entityEvents,
  originData,
  searchValue,
  autoSelectedFields,
  orgId,
  children,
}) => {
  const [sparkData, setSparkData] = useState([{ name: entity.name, data: [] }]);
  const [filteredData, setFilteredData] = useState(entityEvents);
  const [eventCount, setEventCount] = useState(0);
  const [uniqueValues, setUniqueValues] = useState({});
  const [firstPoint, setFirstPoint] = useState(false);
  const [lastPoint, setLastPoint] = useState(false);
  const [logTypes, setLogTypes] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);

  const defaultSelectValue = true;
  const isSelected = (key, value) =>
    autoSelectedFields.includes(key)
      ? String(value) === String(searchValue).toLowerCase()
      : defaultSelectValue;

  useEffect(() => {
    const populatedFields = filteredData
      .map((o) => Object.keys(o))
      .reduce((a, v) => {
        v.forEach((f) => a.add(f));
        return a;
      }, new Set([]));
    const defaultFields = Object.keys(entityFields)
      .filter((k) => populatedFields.has(k))
      .map((k) => {
        return { title: k, field: k, searchable: false };
      });

    setTableColumns([
      {
        title: "Events",
        field: "fromTime",
        searchable: false,
        renderValue: drawHistory,
      },
      { title: "Total", field: "sum_total", searchable: false },
      { title: "Association", field: "associationType", searchable: false },
      ...defaultFields,
    ]);
  }, [entityEvents, filteredData]);

  useEffect(() => {
    const cData = {
      name: entity.name,
      data: [],
      color: entity.color,
    };

    const dt = {};
    entityEvents.forEach((o) => {
      // this code is an attempt at smoothing the data until we can get histogram support in the FEA API
      const startUTS = new moment(o.fromTime).startOf("day");
      const endUTS = new moment(o.toTime).startOf("day");

      const daysDelts = endUTS.diff(startUTS, "days") || 1;

      Array(daysDelts)
        .fill(Number(o.sum_total / daysDelts))
        .forEach((v) => {
          const uts = startUTS.unix() * 1000;
          if (dt[uts] === undefined) {
            dt[uts] = 0;
          }
          dt[uts] += v;
          startUTS.add(1, "days");
        });
    });

    cData.data = Object.keys(dt).map((t) => [Number(t), dt[t]]);
    cData.data.sort((a, b) => a[0] - b[0]);

    setSparkData([cData]);
    setEventCount(cData.data.reduce((_sum, obj) => _sum + obj[1], 0));
    /**
     * We set first and last points together here for a strict reason:
     * later we check is firstPoint is set for rendering assuming that if
     * firstPoint is set, lastPoint will always be set even if its the same
     * point as first
     */
    setFirstPoint(cData.data.find((a) => a[1] > 0));
    setLastPoint(cData.data.findLast((a) => a[1] > 0));
  }, [entityEvents]);

  useEffect(() => {
    const _uniqueValues = {};

    Object.keys(originData).forEach((fieldName) => {
      _uniqueValues[fieldName] = {};
      Object.keys(originData[fieldName]).forEach((uniqueValue) => {
        _uniqueValues[fieldName][uniqueValue] = {
          ...originData[fieldName][uniqueValue],
          checked: isSelected(fieldName, uniqueValue),
        };

        if (uniqueValues[fieldName] && uniqueValues[fieldName][uniqueValue]) {
          _uniqueValues[fieldName][uniqueValue].checked =
            uniqueValues[fieldName][uniqueValue].checked || true;
        }
      });
    });
    setUniqueValues(_uniqueValues);
  }, [originData]);

  useEffect(() => {
    const _logTypes = new Set([]);
    const entityMatches = [];

    const filteredFieldNames = Object.keys(uniqueValues).filter(
      (fieldName) =>
        Object.keys(uniqueValues[fieldName]).filter(
          (value) => uniqueValues[fieldName][value].checked == false
        ).length > 0
    );

    if (Object.keys(uniqueValues).length > 0) {
      const _filteredData = [];
      var filtered;
      const dataIsFiltered = filteredFieldNames.length > 0;

      entityEvents.forEach((row) => {
        if (entity.matchFields.find((e) => row[e.fieldName] !== undefined)) {
          entityMatches.push(row);
          // only count the logtype if its a matched entity
          _logTypes.add(row["type"]);
        }

        filtered = false;

        // if filteredFieldNames.length is 0, bypass all kilter logic
        if (dataIsFiltered) {
          const rowFilterNames = filteredFieldNames.filter(
            (v) => row[v] !== undefined
          );
          // if we have a filter in place (dataIsFiltered) but none of the filter fields
          // are applicable to this model, then the row should be filtered out.
          if (rowFilterNames.length === 0) {
            filtered = true;
          } else {
            // if the row has a field that is on the rowFilterNames list
            // that row must match ALL of the rowFilterNames, or be filtered
            filtered =
              rowFilterNames.length !==
              rowFilterNames.filter(
                (fieldName) =>
                  Object.values(uniqueValues[fieldName])
                    .filter((uVal) => uVal.checked)
                    .filter((uVal) => row[fieldName] === uVal.value).length > 0
              ).length;
          }
        }
        if (!filtered) {
          _filteredData.push(row);
        }
      });
      setFilteredData(_filteredData);
    }
    setLogTypes([..._logTypes]);
  }, [uniqueValues]);

  const getDaysAgo = (dt) => {
    const _now = new moment();
    return _now.diff(moment(dt), "d");
  };

  const handleFilterOnly = ({ model, fieldName }) => {
    Object.keys(uniqueValues[fieldName]).forEach((v) => {
      uniqueValues[fieldName][v].checked =
        uniqueValues[fieldName][v].value === model[fieldName];
    });
    setUniqueValues({ ...uniqueValues });
  };

  const handleFilterOut = ({ model, fieldName }) => {
    uniqueValues[fieldName][model[fieldName]].checked = false;
    setUniqueValues({ ...uniqueValues });
  };

  const drawHistory = (column, model) => {
    const _sData = {
      name: "history",
      data: model.sparkline?.map((s) => [s.start, s.value]),
    };
    return <Spark data={[_sData]} height={50} />;
  };

  return (
    <StyledAccordion disabled={!firstPoint}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs={2} lg={2}>
            <Typography variant="h6">{entity.name}</Typography>
          </Grid>
          {firstPoint ? (
            <>
              <Grid item xs={1} lg={1}>
                <Typography variant="caption" display="block" gutterBottom>
                  Events
                </Typography>
                <Typography variant="h2" gutterBottom>
                  {abbreviateNumber(eventCount)}
                </Typography>
              </Grid>
              <Grid item xs={4} lg={4}>
                <Typography variant="caption" display="block" gutterBottom>
                  {firstPoint &&
                    moment(lastPoint[0]).diff(moment(firstPoint[0]), "d")}{" "}
                  days
                </Typography>
                <Spark data={sparkData} />
              </Grid>
              <Grid item xs={2} lg={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Most Recent
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {lastPoint && getDaysAgo(lastPoint[0])} days ago
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  className={overviewClasses.italic}
                >
                  {lastPoint &&
                    moment(lastPoint[0]).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Grid>
              <Grid item xs={2} lg={2}>
                <Typography variant="caption" display="block" gutterBottom>
                  Oldest
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {firstPoint && getDaysAgo(firstPoint[0])} days ago
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  className={overviewClasses.italic}
                >
                  {firstPoint &&
                    moment(firstPoint[0]).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Grid>
              <Grid item xs={1} lg={1}>
                <Typography variant="caption" display="block" gutterBottom>
                  Data Sources
                </Typography>
                <Typography variant="h2" gutterBottom>
                  {logTypes.length}
                </Typography>
              </Grid>
            </>
          ) : (
            <Typography>No data available for selected time window</Typography>
          )}
        </Grid>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs={12} lg={12}>
            <OverviewContolBar
              fieldData={uniqueValues}
              setOverviewContext={setUniqueValues}
            />
          </Grid>
          {/**<SSDig data={sparkData} /> */}
          <Grid item xs={12} lg={12}>
            <SimpleTable
              isSearchHidden={true}
              data={filteredData}
              columns={tableColumns}
              contextMenuItems={[
                {
                  onClick: ({ model, fieldName }) =>
                    navToInvestigation(orgId, { search: model[fieldName] }),
                  limitToFields: Object.keys(entityFields),
                  text: "Investigate in new window",
                },
                {
                  onClick: handleFilterOnly,
                  limitToFields: Object.keys(entityFields),
                  text: "Include",
                },
                {
                  onClick: handleFilterOut,
                  limitToFields: Object.keys(entityFields),
                  text: "Exclude",
                },
              ]}
            />
          </Grid>
          <>{children}</>
        </Grid>
      </AccordionDetails>
      <Divider />
      <AccordionActions></AccordionActions>
    </StyledAccordion>
  );
};
